import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Logger from "../../services/Logger";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScoreIcon from "@mui/icons-material/Score";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import StatsService from "../../services/StatsGridService";
import UtilService from "../../services/UtilService";
import CustomSnackbar from "../components/CustomSnackbar";
import DifficultyLevelPolicyDialog from "./board/DifficultyLevelPolicyDialog";
import DifficultyAnalysis from "./DifficultyAnalysis";
import WordScoreDialog from "./WordScoreDialog";
import AdminCrossBoardService from "../pzl_cross/board/AdminCrossBoardService";
import DictionaryApiService from "../system/integrate/DictionaryApiService";
import AdminGridBoardService from "../pzl_grid/board/AdminGridBoardService";
import AdminStoryService from "../story/AdminStoryService";
import StoryImageZoomDialog from "../story/StoryImageZoomDialog";
import WordWheelCharacters from "../pzl_wordwheel/board/WordWheelCharacters";
import { getLocalizedStoryFromTemplate } from "./getEnStoryFromTemplate";

function parsePuzzleDetails(puzzle) {
    if (!puzzle?.details) return null;
    if (typeof puzzle.details === "string") {
        try {
            return JSON.parse(puzzle.details);
        } catch (error) {
            Logger.error("Error parsing puzzle details:", error);
            return null;
        }
    }
    return puzzle.details;
}

function extractTotalScore(details) {
    if (!details || typeof details !== "object") return null;
    const direct = details.totalScore;
    if (direct != null && direct !== "" && Number.isFinite(Number(direct))) {
        return Number(direct);
    }
    const nested = details.wordScores?.totalScore;
    if (nested != null && nested !== "" && Number.isFinite(Number(nested))) {
        return Number(nested);
    }
    return null;
}

export default function BorderLeftPanel({ puzzle, onRefresh, puzzleType }) {
    // Internal state for difficulty level
    const [difficultyLevel, setDifficultyLevel] = useState("EASY");

    // Internal state for puzzle statistics
    const [stats, setStats] = useState({
        total: 0,
        completed: 0,
        successRate: 0
    });
    const [statsLoading, setStatsLoading] = useState(false);

    // Internal state for error handling
    const [error] = useState(null);
    const wordList = useMemo(() => {
        return puzzle?.wordsInUse ? puzzle.wordsInUse.split('\n').filter(word => word.trim()) : [];
    }, [puzzle?.wordsInUse]);

    const filledCoordinates = useMemo(() => {
        if (!puzzle?.filledCoordinates) return [];
        try {
            return JSON.parse(puzzle.filledCoordinates);
        } catch (error) {
            Logger.error("Error parsing filledCoordinates:", error);
            return [];
        }
    }, [puzzle?.filledCoordinates]);

    const totalScore = useMemo(() => extractTotalScore(parsePuzzleDetails(puzzle)), [puzzle?.details]);

    const isWordWheel = puzzleType === "WORD_WHEEL" || puzzle?.type === "WORD_WHEEL";
    const showWordWheelTotalScore = isWordWheel
        && (puzzle?.state === "REVIEWED" || puzzle?.state === "PUBLISHED")
        && totalScore != null;

    // Extract hidden message from filledCoordinates if puzzle type is HIDDEN_MESSAGE
    const hiddenMessage = useMemo(() => {
        if (puzzle?.type === "HIDDEN_MESSAGE" && puzzle?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(puzzle.filledCoordinates);
                const scatteredWord = filledCoordinates.find(coord => coord.direction === "scattered");
                return scatteredWord?.text || puzzle?.hiddenMessage || null;
            } catch (error) {
                Logger.error("Error parsing filledCoordinates for hidden message:", error);
                return puzzle?.hiddenMessage || null;
            }
        }
        return puzzle?.hiddenMessage || null;
    }, [puzzle?.type, puzzle?.filledCoordinates, puzzle?.hiddenMessage]);

    const storyPreview = useMemo(() => {
        if (!puzzle || typeof puzzle !== "object") return { title: "", imageUrl: "", storyId: "" };
        const { title, imageUrl } = getLocalizedStoryFromTemplate(puzzle);
        let storyId = "";
        const rawStoryId = puzzle.story?.id ?? puzzle.story?.storyId;
        if (rawStoryId != null && String(rawStoryId).trim()) {
            storyId = String(rawStoryId).trim();
        }
        return {
            title: typeof title === "string" ? title.trim() : "",
            imageUrl: typeof imageUrl === "string" ? imageUrl.trim() : "",
            storyId,
        };
    }, [puzzle]);

    const hasStoryPreview = Boolean(storyPreview.title || storyPreview.imageUrl);
    const [scoreStats, setScoreStats] = useState({
        lowScorePercentage: null,          // Percentage of words with score between hardScoreMin and hardScoreMax
        easyScorePercentage: null,         // Percentage of words with score between easyScoreMin and easyScoreMax
        totalWordsWithScore: null         // Total words that have score data
    });
    const [scoreLoading, setScoreLoading] = useState(false);
    const [difficultyPolicy, setDifficultyPolicy] = useState(null);

    // Dialog state for showing policies
    const [openPolicyDialog, setOpenPolicyDialog] = useState(false);

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Refresh loading state
    const [refreshing, setRefreshing] = useState(false);
    const [resettingStory, setResettingStory] = useState(false);
    const [storyImageZoomOpen, setStoryImageZoomOpen] = useState(false);

    // Word Score dialog state
    const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
    const [wordScores, setWordScores] = useState([]);
    const [loadingScores, setLoadingScores] = useState(false);

    // Initialize difficulty level from puzzle data
    useEffect(() => {
        if (puzzle) {
            setDifficultyLevel(puzzle.difficultyLevel || "EASY");
        }
    }, [puzzle]);

    // Fetch puzzle statistics
    useEffect(() => {
        const fetchStats = async () => {
            if (!puzzle?.id) return;

            try {
                setStatsLoading(true);
                // Use puzzle.type if available, otherwise fall back to puzzleType prop, or default to WORD_SEARCH
                const actualPuzzleType = puzzle?.type || puzzleType || 'WORD_SEARCH';
                const statsData = await StatsService.getPuzzleStats(puzzle.id, actualPuzzleType);
                setStats({
                    total: statsData?.total ?? 0,
                    completed: statsData?.completed ?? 0,
                    successRate: statsData?.successRate ?? 0
                });
            } catch (err) {
                Logger.error("Error fetching puzzle stats:", err);
                // Keep default values on error
            } finally {
                setStatsLoading(false);
            }
        };

        fetchStats();
    }, [puzzle?.id, puzzle?.type, puzzleType]);

    // Handle difficulty level change

    // Fetch word scores and difficulty level information
    useEffect(() => {
        const fetchWordScoresAndDifficulty = async () => {
            if (wordList.length === 0) {
                setScoreStats({
                    lowScorePercentage: null,
                    easyScorePercentage: null,
                    totalWordsWithScore: null
                });
                setDifficultyPolicy(null);
                return;
            }

            try {
                setScoreLoading(true);
                const language = puzzle?.language || "english";
                
                // Fetch difficulty level which includes policy
                const difficultyResult = await DictionaryApiService.getDifficultyLevel(wordList, language);
                
                if (difficultyResult && difficultyResult.puzzleConfigure) {
                    setDifficultyPolicy(difficultyResult);
                    
                    // Fetch word scores to calculate statistics
                    const wordsInfo = await DictionaryApiService.getMultipleWordsInfo(wordList, language);
                    
                    // Calculate statistics based on scores
                    const wordsWithScore = wordsInfo.filter(word => word.info && word.info.score !== null && word.info.score !== undefined);
                    
                    if (wordsWithScore.length > 0 && difficultyResult.puzzleConfigure) {
                        const config = difficultyResult.puzzleConfigure;
                        const scores = wordsWithScore.map(word => parseInt(word.info.score));
                        
                        // Count words in HARD range (hardScoreMin to hardScoreMax)
                        const hardCount = scores.filter(score => 
                            score >= config.hardScoreMin && score <= config.hardScoreMax
                        ).length;
                        const lowScorePercentage = (hardCount / wordsWithScore.length) * 100;
                        
                        // Count words in EASY range (easyScoreMin to easyScoreMax)
                        const easyCount = scores.filter(score => 
                            score >= config.easyScoreMin && score <= config.easyScoreMax
                        ).length;
                        const easyScorePercentage = (easyCount / wordsWithScore.length) * 100;

                        setScoreStats({
                            lowScorePercentage,
                            easyScorePercentage,
                            totalWordsWithScore: wordsWithScore.length
                        });
                    } else {
                        setScoreStats({
                            lowScorePercentage: null,
                            easyScorePercentage: null,
                            totalWordsWithScore: null
                        });
                    }
                } else {
                    // Fallback: fetch policy separately if not in difficulty result
                    try {
                        const policyResult = await DictionaryApiService.getDifficultyLevelPolicy();
                        if (policyResult && policyResult.puzzleConfigure) {
                            setDifficultyPolicy(policyResult);
                        }
                    } catch (error) {
                        Logger.error("Error fetching difficulty level policy:", error);
                    }
                    setScoreStats({
                        lowScorePercentage: null,
                        easyScorePercentage: null,
                        totalWordsWithScore: null
                    });
                }
            } catch (error) {
                Logger.error("Error fetching word scores and difficulty:", error);
                setScoreStats({
                    lowScorePercentage: null,
                    easyScorePercentage: null,
                    totalWordsWithScore: null
                });
                setDifficultyPolicy(null);
            } finally {
                setScoreLoading(false);
            }
        };

        fetchWordScoresAndDifficulty();
    }, [wordList, puzzle?.language]);

    // Handle info button click
    const handleInfoClick = () => {
        setOpenPolicyDialog(true);
    };

    // Handle refresh button click
    const handleRefreshClick = async () => {
        if (!puzzle?.id) return;

        try {
            setRefreshing(true);
            // Use the appropriate service based on puzzleType
            const service = puzzleType === 'CROSSWORD' ? AdminCrossBoardService : AdminGridBoardService;
            const result = await service.refreshDifficultyLevel(puzzle.id);
            
            if (result.code && result.code === "FAILURE") {
                setSnackbarMessage(result.message || "Failed to refresh difficulty level");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage("Difficulty level refreshed successfully");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                
                // Update difficulty level if returned in response
                if (result.difficultyLevel) {
                    setDifficultyLevel(result.difficultyLevel);
                }
                
                // Delay the refresh callback to allow snackbar to display first
                // Call onRefresh callback if provided to refresh puzzle data
                if (onRefresh) {
                    setTimeout(() => {
                        onRefresh();
                    }, 500); // Small delay to ensure snackbar is visible
                }
            }
        } catch (err) {
            Logger.error("Error refreshing difficulty level:", err);
            setSnackbarMessage("Failed to refresh difficulty level. Please try again.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setRefreshing(false);
        }
    };

    const handleResetStory = async () => {
        const storyId = storyPreview.storyId;
        if (!storyId || resettingStory) return;
        const confirmed = window.confirm(
            "Reset this story? This removes the story image and deletes linked jigsaw and sliding puzzles. Auto story generation will stay off for this template."
        );
        if (!confirmed) return;
        setResettingStory(true);
        try {
            const result = await AdminStoryService.deleteById(storyId);
            if (!result.success) {
                setSnackbarMessage(result.error || "Failed to reset story");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }
            setSnackbarMessage("Story reset — image, jigsaw, and sliding removed");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            if (typeof onRefresh === "function") {
                setTimeout(() => onRefresh(), 300);
            }
        } catch (err) {
            Logger.error("Error resetting story:", err);
            setSnackbarMessage(err?.message || "Failed to reset story");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setResettingStory(false);
        }
    };

    // Handle score button click
    const handleScoreClick = async () => {
        if (wordList.length === 0) {
            setSnackbarMessage("No words available to fetch scores");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }

        try {
            setLoadingScores(true);
            setScoreDialogOpen(true);
            const result = await DictionaryApiService.getMultipleWordsInfo(wordList, puzzle?.language || "english");
            
            if (result && Array.isArray(result)) {
                setWordScores(result);
            } else {
                setWordScores([]);
            }
        } catch (error) {
            Logger.error("Error fetching word scores:", error);
            setSnackbarMessage("Failed to fetch word scores. Please try again.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setScoreDialogOpen(false);
            setWordScores([]);
        } finally {
            setLoadingScores(false);
        }
    };

    // Handle word score dialog refresh
    const handleWordScoreRefresh = async () => {
        if (wordList.length === 0) return;

        try {
            setLoadingScores(true);
            const result = await DictionaryApiService.getMultipleWordsInfo(wordList, puzzle?.language || "english");
            
            if (result && Array.isArray(result)) {
                setWordScores(result);
                setScoreDialogOpen(true);
            } else {
                setWordScores([]);
            }
        } catch (error) {
            Logger.error("Error refreshing word scores:", error);
            setWordScores([]);
        } finally {
            setLoadingScores(false);
        }
    };

    return (
        <Paper elevation={2} sx={{ p: 3, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                Puzzle Information
            </Typography>

            {/* Error Display */}
            {error && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: "error.light", borderRadius: 1 }}>
                    <Typography variant="body2" color="error.contrastText">
                        {error}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Creator
                        </Typography>
                        <Typography variant="body1">
                            {(() => {
                                const creator = puzzle?.creator || puzzle?.cloudUser;
                                if (creator) {
                                    const firstName = creator.firstName || "";
                                    const lastName = creator.lastName || "";
                                    return [firstName, lastName].filter(Boolean).join(" ").trim() || "Not specified";
                                }
                                return puzzle?.creatorName || "Not specified";
                            })()}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Puzzle Type
                        </Typography>
                        <Typography variant="body1">
                            {puzzle?.type || "Not specified"}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Puzzle Name
                        </Typography>
                        <Typography variant="body1">
                            {puzzle?.name || "Not specified"}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Created At
                        </Typography>
                        <Typography variant="body1">
                            {puzzle?.createdAt ? UtilService.formatDate(puzzle.createdAt) : "Not specified"}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Published At
                        </Typography>
                        <Typography variant="body1">
                            {puzzle?.publishedAt ? UtilService.formatDate(puzzle.publishedAt) : "Not specified"}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography variant="subtitle2" color="text.secondary" align="left">
                                    Difficulty Level
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
                                    <Tooltip title="View difficulty level policies">
                                        <IconButton
                                            size="small"
                                            onClick={handleInfoClick}
                                        >
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Refresh difficulty level">
                                        <IconButton
                                            size="small"
                                            onClick={handleRefreshClick}
                                            disabled={refreshing || !puzzle?.id}
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View word scores">
                                        <IconButton
                                            size="small"
                                            onClick={handleScoreClick}
                                            disabled={loadingScores || wordList.length === 0}
                                        >
                                            <ScoreIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label="EASY"
                                    color={difficultyLevel === "EASY" ? "success" : "default"}
                                    variant={difficultyLevel === "EASY" ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ fontWeight: difficultyLevel === "EASY" ? 'bold' : 'normal' }}
                                />
                                <Chip
                                    label="MEDIUM"
                                    color={difficultyLevel === "MEDIUM" ? "warning" : "default"}
                                    variant={difficultyLevel === "MEDIUM" ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ fontWeight: difficultyLevel === "MEDIUM" ? 'bold' : 'normal' }}
                                />
                                <Chip
                                    label="HARD"
                                    color={difficultyLevel === "HARD" ? "error" : "default"}
                                    variant={difficultyLevel === "HARD" ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ fontWeight: difficultyLevel === "HARD" ? 'bold' : 'normal' }}
                                />
                            </Box>
                            <DifficultyAnalysis 
                                scoreLoading={scoreLoading}
                                scoreStats={scoreStats}
                                policy={difficultyPolicy}
                            />
                        </Box>
                    </Box>
                    {puzzle?.type === "HIDDEN_MESSAGE" && hiddenMessage && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Hidden message
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.25rem', color: 'primary.main', letterSpacing: 1 }}>
                                {hiddenMessage.toUpperCase()}
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* Story (crossword / grid template narrative + illustration) */}
            {hasStoryPreview ? (
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#3BA666" }}>
                            Story
                        </Typography>
                        {storyPreview.storyId && (
                            <Tooltip title="Open story page" arrow>
                                <IconButton
                                    component={RouterLink}
                                    to={`/admin/story/${storyPreview.storyId}`}
                                    size="small"
                                    sx={{ color: "#3BA666" }}
                                >
                                    <AutoStoriesIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {storyPreview.storyId ? (
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={resettingStory}
                                onClick={handleResetStory}
                                sx={{ ml: "auto", minHeight: 36, textTransform: "none" }}
                            >
                                {resettingStory ? "Resetting…" : "Reset"}
                            </Button>
                        ) : null}
                    </Box>
                    {storyPreview.title ? (
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0 }}>
                            {storyPreview.title}
                        </Typography>
                    ) : null}
                    {storyPreview.storyId ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: "block",
                                mt: storyPreview.title ? 0.25 : 0,
                                mb: storyPreview.imageUrl ? 1 : 0,
                                wordBreak: "break-all",
                                fontFamily: "ui-monospace, monospace",
                            }}
                        >
                            Story ID: {storyPreview.storyId}
                        </Typography>
                    ) : null}
                    {storyPreview.imageUrl ? (
                        <Box
                            sx={{
                                width: "100%",
                                bgcolor: "action.hover",
                                borderRadius: 1,
                                border: 1,
                                borderColor: "divider",
                                boxSizing: "border-box",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                m: 0,
                                position: "relative",
                            }}
                        >
                            <Box
                                component="img"
                                src={storyPreview.imageUrl}
                                alt={storyPreview.title || "Story illustration"}
                                onClick={() => setStoryImageZoomOpen(true)}
                                sx={{
                                    width: 200,
                                    height: 200,
                                    objectFit: "contain",
                                    display: "block",
                                    flexShrink: 0,
                                    m: 0,
                                    cursor: "zoom-in",
                                }}
                            />
                            <Tooltip title="View full size" arrow>
                                <IconButton
                                    size="small"
                                    aria-label="View full size story image"
                                    onClick={() => setStoryImageZoomOpen(true)}
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        minWidth: 44,
                                        minHeight: 44,
                                        bgcolor: "rgba(255,255,255,0.92)",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                    }}
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : null}
                </Box>
            ) : null}

            {/* Publishing Statistics */}
            {stats && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                        Publishing Statistics
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {statsLoading ? "..." : (stats.total ?? 0).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Plays
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="success.main">
                                    {statsLoading ? "..." : (stats.completed ?? 0).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completed
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="info.main">
                                    {statsLoading ? "..." : `${(stats.successRate ?? 0).toFixed(1)}%`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Success Rate
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {isWordWheel && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", color: "#3BA666" }}>
                        Word Wheel
                    </Typography>
                    <Grid container spacing={1.5} alignItems="flex-start">
                        <Grid size={{ xs: 12, sm: showWordWheelTotalScore ? 6 : 12 }}>
                            <WordWheelCharacters
                                embedded
                                compact
                                filledCoordinates={filledCoordinates}
                                wordsInUse={puzzle?.wordsInUse}
                            />
                        </Grid>
                        {showWordWheelTotalScore && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#3BA666", mb: 0.75 }}>
                                    Total Score
                                </Typography>
                                <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                                    {totalScore.toLocaleString()}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {/* Word Score Dialog */}
            <WordScoreDialog
                open={scoreDialogOpen}
                onClose={() => setScoreDialogOpen(false)}
                wordScores={wordScores}
                loadingScores={loadingScores}
                language={puzzle?.language || "english"}
                onRefresh={handleWordScoreRefresh}
            />

            {/* Policy Dialog */}
            <DifficultyLevelPolicyDialog
                open={openPolicyDialog}
                onClose={() => setOpenPolicyDialog(false)}
            />

            {/* Custom Snackbar */}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />

            <StoryImageZoomDialog
                open={storyImageZoomOpen && Boolean(storyPreview.imageUrl)}
                imageUrl={storyPreview.imageUrl || ""}
                title={storyPreview.title || "Story illustration"}
                onClose={() => setStoryImageZoomOpen(false)}
            />
        </Paper>
    );
}

